import type { AbortSignal as SdkAbortSignal } from "@aws-sdk/types";
import { ListClaimsInput, ListClaimsCommand } from "@amzn/ttechclaimintakeservice-client";

import { getAuthenticatedClient } from "../setup/getAuthenticatedClient";
import { MetricsPublisher } from "../../metrics/metrics";

/**
 * Lists claims through the generic config-driven retrieval operation, where
 * `claimType` selects the table and query configuration and `queryName` selects
 * the named query to run.
 *
 * An optional `AbortSignal` cancels the in-flight request so a superseded or
 * timed-out search stops consuming a Lambda invocation instead of running to
 * completion. When aborted, the `AbortError` is re-thrown so callers can tell
 * a cancellation apart from a real service failure.
 */
export async function listClaimsService(input: ListClaimsInput, signal?: AbortSignal) {
  const { client } = await getAuthenticatedClient();
  if (!client) {
    return null;
  }
  const command = new ListClaimsCommand(input);
  const metricsPublisher = new MetricsPublisher("api.listClaims");

  return await client
    .send(command, { abortSignal: signal as unknown as SdkAbortSignal })
    .then((response) => {
      metricsPublisher.publishAuthenticationLatencyMetrics();
      metricsPublisher.publishRenderSuccessMetrics();
      return response;
    })
    .catch((error: unknown) => {
      // A cancellation is caller-initiated, so publishing a failure metric here
      // would report an operational problem that did not happen.
      if (error instanceof Error && error.name === "AbortError") {
        throw error;
      }
      metricsPublisher.publishAuthenticationLatencyMetrics();
      metricsPublisher.publishRenderFatalMetrics();
      throw error;
    });
}
